import React, { useMemo } from 'react';
import { useOrganizationId } from '../../../../hooks/api/useOrganization';
import useNotes, { NoteQueryArgs } from '../../../../hooks/api/useNotes';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { APITypes } from '../../../../primary-record-types';
import { SelectableNoteCard } from '../../NoteCard';
import NoteSearch from '../../Notes/NotesList/NoteSearch';
import HeaderContainer from '../../../v2BaseComponents/HeaderContainer';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';
import { PRScrollView } from '../../Scroll';

export type UseCopyOrganizationNoteToClientsStateReturnValue = {
    isInitializing: boolean;
    isSubmitting: boolean;
    selectedNoteIds: string[];
    selectedNotes: APITypes.notesService.NoteListItem[];
    noteSelectOptions: APITypes.notesService.NoteListItem[];
    onSubmit: () => void;
    canSubmit: boolean;
    onNoteSelection: (noteId: string) => void;
};

export type UseCopyOrganizationNoteToClientsStateArgs = {
    onClose: () => void;
    destinationMedicalProfileIds: string[];
};

export const useCopyOrganizationNoteToClientsState = (
    args: UseCopyOrganizationNoteToClientsStateArgs,
): UseCopyOrganizationNoteToClientsStateReturnValue => {
    const organizationId = useOrganizationId();
    const [selectedNoteIds, setSelectedNoteIds] = React.useState<string[]>([]);
    const noteQueryArgs = useMemo<NoteQueryArgs>(
        () => ({
            entityType: 'organization',
            organizationId,
        }),
        [organizationId],
    );
    const { isLoading, notes, copyNotesToProfiles, isCopyingNoteToProfiles } = useNotes({
        noteQueryArgs,
        onNoteCopyToProfilesSuccess: args.onClose,
    });
    const sortedNotes = useMemo(() => {
        return [...(notes.notes || [])].sort((a, b) => {
            return a.createdAt > b.createdAt ? -1 : 1;
        });
    }, [notes.notes]);
    const selectedNotes = useMemo(() => {
        return notes.notes?.filter((note) => selectedNoteIds.includes(note.noteId)) || [];
    }, [notes.notes, selectedNoteIds]);

    return {
        isInitializing: isLoading,
        isSubmitting: isCopyingNoteToProfiles,
        selectedNoteIds,
        selectedNotes,
        noteSelectOptions: sortedNotes,
        onSubmit: () => {
            if (selectedNoteIds.length) {
                copyNotesToProfiles({
                    noteIds: selectedNoteIds,
                    destinationMedicalProfileIds: args.destinationMedicalProfileIds,
                    organizationId,
                });
            }
        },
        canSubmit: !!selectedNoteIds.length && !isCopyingNoteToProfiles,
        onNoteSelection: (noteId: string) => {
            setSelectedNoteIds((prev) => {
                if (prev.includes(noteId)) {
                    return prev.filter((id) => id !== noteId);
                }
                return [...prev, noteId];
            });
        },
    };
};

export type UseCopyOrganizationNoteToClientStateArgs = {
    onClose: () => void;
    destinationMedicalProfileId: string;
    noteDestination: APITypes.notesService.CopyOrganizationNoteToMedicationProfileRequest;
};

export type UseCopyOrganizationNoteToClientStateReturnValue = UseCopyOrganizationNoteToClientsStateReturnValue;

export const useCopyOrganizationNoteToClientState = (
    args: UseCopyOrganizationNoteToClientStateArgs,
): UseCopyOrganizationNoteToClientStateReturnValue => {
    const organizationId = useOrganizationId();
    const [selectedNoteIds, setSelectedNoteIds] = React.useState<string[]>([]);
    const noteQueryArgs = useMemo<NoteQueryArgs>(
        () => ({
            entityType: 'organization',
            organizationId,
        }),
        [organizationId],
    );
    const { isLoading, notes, copyOrgNotesToClientProfile, isCopyingOrgNotesToClientProfile } = useNotes({
        noteQueryArgs,
        onNoteCopyToClientSuccess: args.onClose,
    });
    const sortedNotes = useMemo(() => {
        return [...(notes.notes || [])].sort((a, b) => {
            return a.createdAt > b.createdAt ? -1 : 1;
        });
    }, [notes.notes]);
    const selectedNotes = useMemo(() => {
        return notes.notes?.filter((note) => selectedNoteIds.includes(note.noteId)) || [];
    }, [notes.notes, selectedNoteIds]);

    return {
        isInitializing: isLoading,
        isSubmitting: isCopyingOrgNotesToClientProfile,
        selectedNoteIds,
        selectedNotes,
        noteSelectOptions: sortedNotes,
        onSubmit: () => {
            if (selectedNoteIds.length) {
                copyOrgNotesToClientProfile({
                    noteIds: selectedNoteIds,
                    destinationMedicalProfileId: args.destinationMedicalProfileId,
                    destinationArgs: args.noteDestination,
                    organizationId,
                });
            }
        },
        canSubmit: !!selectedNoteIds.length && !isCopyingOrgNotesToClientProfile,
        onNoteSelection: (noteId: string) => {
            setSelectedNoteIds((prev) => {
                if (prev.includes(noteId)) {
                    return prev.filter((id) => id !== noteId);
                }
                return [...prev, noteId];
            });
        },
    };
};

export type CopyOrganizationNotesToClientsOwnProps = {
    selectedNoteIds: string[];
    noteSelectOptions: APITypes.notesService.NoteListItem[];
    onNoteSelection: (noteId: string) => void;
    isDisabled?: boolean;
    flatCards?: boolean;
};

export type CopyOrganizationNotesToClientsProps = CopyOrganizationNotesToClientsOwnProps;

export type UseCopyOrganizationNoteToClientsState = (
    props: CopyOrganizationNotesToClientsProps,
) => UseCopyOrganizationNoteToClientsStateReturnValue;

const CopyOrganizationNotesToClients: React.FC<CopyOrganizationNotesToClientsProps> = ({
    selectedNoteIds,
    noteSelectOptions,
    onNoteSelection,
    isDisabled,
    flatCards,
}) => {
    const [searchText, setSearchText] = React.useState('');
    const filteredNotes = useMemo(() => {
        return noteSelectOptions.filter((note) => {
            return note.title.toLowerCase().includes(searchText.toLowerCase());
        });
    }, [noteSelectOptions, searchText]);

    return (
        <Stack vertical fullWidth fullHeight>
            <HeaderContainer>
                <NoteSearch
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    style={{ width: '100%' }}
                    isDisabled={isDisabled}
                    testID="note-share-search"
                />
            </HeaderContainer>
            <PRScrollView style={{ width: '100%', minHeight: 0 }}>
                <MainContentContainer>
                    <Stack
                        vertical
                        fullWidth
                        space={contentStackSpacing}
                        flex={1}
                        style={{ minHeight: '100%' }}
                        justify="space-between"
                    >
                        {(filteredNotes || []).map((note) => (
                            <SelectableNoteCard
                                key={note.noteId}
                                note={note}
                                isSelected={selectedNoteIds.includes(note.noteId)}
                                onSelect={() => onNoteSelection(note.noteId)}
                                isSelectable
                                isDisabled={isDisabled}
                                flat={flatCards}
                                testID="copy-organization-note-to-clients-note-card"
                            />
                        ))}
                    </Stack>
                </MainContentContainer>
            </PRScrollView>
        </Stack>
    );
};

export default React.memo(CopyOrganizationNotesToClients);
